import { useMemo } from "react";
import { useGetList, useTranslate } from "ra-core";
import { ResponsiveBar } from "@nivo/bar";
import { Skeleton } from "@/components/ui/skeleton";
import type { Transaction } from "../../types";

const MONTHS = 12;

const monthKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export const MonthlyCashflowChart = () => {
  const translate = useTranslate();
  const since = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - (MONTHS - 1));
    d.setDate(1);
    return d;
  }, []);
  const sinceIso = since.toISOString().slice(0, 10);

  const { data, isPending } = useGetList<Transaction>("transactions", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "occurred_at", order: "ASC" },
    filter: { "occurred_at@gte": sinceIso, "transfer_id@is": null },
  });

  const chartData = useMemo(() => {
    const buckets: Record<string, { month: string; income: number; expense: number }> = {};
    for (let i = 0; i < MONTHS; i++) {
      const d = new Date(since);
      d.setMonth(since.getMonth() + i);
      const key = monthKey(d);
      buckets[key] = { month: key, income: 0, expense: 0 };
    }
    for (const tx of data ?? []) {
      const key = monthKey(new Date(tx.occurred_at));
      if (!buckets[key]) continue;
      if (tx.direction === "income") buckets[key].income += Number(tx.amount);
      else buckets[key].expense += Number(tx.amount);
    }
    return Object.values(buckets);
  }, [data, since]);

  if (isPending) return <Skeleton className="h-64 w-full" />;
  if (!chartData.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("finance.dashboard.noData", { _: "Нет данных" })}
      </p>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveBar
        data={chartData}
        keys={["income", "expense"]}
        indexBy="month"
        margin={{ top: 16, right: 16, bottom: 40, left: 56 }}
        groupMode="grouped"
        padding={0.25}
        colors={["#16a34a", "#dc2626"]}
        axisBottom={{ tickRotation: -30 }}
        axisLeft={{
          format: (v) => `${(Number(v) / 1000).toFixed(0)}k`,
        }}
        enableLabel={false}
        animate={false}
        tooltip={({ id, value, indexValue }) => (
          <div className="rounded-md border bg-popover px-2 py-1 text-xs shadow">
            {indexValue} · {String(id)}: {Number(value).toLocaleString("ru-RU")} ₽
          </div>
        )}
      />
    </div>
  );
};
